import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React from "react";
import { useHistory } from "@core/routing/useHistory";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useAsync } from "@utils/hooks";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { TertiaryButton } from "@ui/components/buttons/tertiaryButton";
import { row, Spacer, stack } from "@ui/components/layout";
import { desktop, mobile } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { useGroupEditorController } from "@ui/pages/group/groupEditorPage/groupEditorController/groupEditorControllerContext";

interface GroupEditorNavigationProps extends React.HTMLAttributes<HTMLDivElement> {
	groupId?: string;
	newMemberIds?: string[];
}

export const GroupEditorNavigation: React.FC<GroupEditorNavigationProps> = ({
	groupId,
	newMemberIds,
	...htmlProps
}) => {
	const controller = useGroupEditorController();
	const group = useObservable(controller.group);
	const step = useObservable(controller.step);
	const isValid = useObservable(controller.isValid);
	const history = useHistory();

	const { groupService } = useServices();

	const { run, loading } = useAsync(
		async (showToast?: boolean) => {
			const fetchedGroup = await groupService.save(group, groupId, newMemberIds);
			history.push({
				pathname:
					fetchedGroup.type === "SHAREROOM"
						? Path.Shareroom(fetchedGroup, undefined, undefined)
						: Path.Discussion(fetchedGroup),
				state: groupId ? undefined : "NEW",
			});
			if (showToast) {
				createToast(I18n.t("success"), I18n.t(`group.${group.type}.save.success`), "success");
			}
		},
		[group, newMemberIds]
	);

	return (
		<Container {...htmlProps}>
			{step === "MEMBERS" && (
				<PreviousStepButton onClick={() => controller.previous()}>{I18n.t("back")}</PreviousStepButton>
			)}
			<Spacer />
			{groupId || step === "MEMBERS" ? (
				<LoadingButton disabled={!isValid} loading={loading} onClick={() => run(true)}>
					{I18n.t(groupId ? "validate" : "create")}
				</LoadingButton>
			) : (
				<PrimaryButton onClick={() => controller.next()} disabled={!isValid}>
					{I18n.t("next")}
				</PrimaryButton>
			)}
		</Container>
	);
};

const Container = styled.div`
	@media ${desktop} {
		${row(12, "flex-end", "center")};
	}
	@media ${mobile} {
		${stack(10, "center", "center", "reverse")};
	}
`;

const SaveDraftButton = styled(TertiaryButton)`
	text-decoration: none;
	${({ theme }) => theme.textStyles.label};
	font-size: 0.9375rem;
`;
const PreviousStepButton = styled(SaveDraftButton)`
	color: ${({ theme }) => theme.colors.primary};

	&:hover {
		color: ${({ theme }) => theme.colors.primaryLight};
	}
`;
